// History-API router. Real URLs, no hash routing — Nginx serves the shell on
// any unmatched path (migration-plan.md §3.3).

export interface RouteContext {
  path: string;
  params: Record<string, string>;
  query: URLSearchParams;
}

export type View = (outlet: HTMLElement, ctx: RouteContext) => void | Promise<void>;

interface Route {
  pattern: string;
  segments: string[];
  view: View;
}

const routes: Route[] = [];
let outlet: HTMLElement;
let notFound: View = (mount) => {
  mount.textContent = 'Not found';
};

export function route(pattern: string, view: View): void {
  routes.push({ pattern, segments: pattern.split('/').filter(Boolean), view });
}

export function setNotFound(view: View): void {
  notFound = view;
}

/**
 * A redirect gate run before EVERY render, not just at boot.
 *
 * It exists because the signed-out bounce to /login used to live in `boot()`,
 * so it fired once on a cold load and never again -- and the SPA never reloads.
 * A signed-out visitor who followed any in-app link to a private path therefore
 * got that view rendering against an API that answers 401: /register's logo
 * link landed on the dashboard's "Could not load your dashboard" instead of the
 * login screen it was pointing at. Nothing threw, and the URL looked right.
 *
 * Returns the path to go to instead, or null to let the render proceed.
 */
let guard: ((path: string) => string | null) | null = null;

export function setGuard(fn: (path: string) => string | null): void {
  guard = fn;
}

function match(path: string): { view: View; params: Record<string, string> } {
  const parts = path.split('/').filter(Boolean);
  for (const r of routes) {
    if (r.segments.length !== parts.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < r.segments.length; i++) {
      const seg = r.segments[i]!;
      const val = parts[i]!;
      if (seg.startsWith(':')) params[seg.slice(1)] = decodeURIComponent(val);
      else if (seg !== val) {
        ok = false;
        break;
      }
    }
    if (ok) return { view: r.view, params };
  }
  return { view: notFound, params: {} };
}

/**
 * How many in-app entries sit behind the current one.
 *
 * Carried in `history.state` so it survives Back and Forward. The header's back
 * chevron reads it: above zero, `history.back()` stays inside the app; at zero
 * the entry behind this one is another site, or nothing (a cold load from a
 * link in an email), so the chevron goes UP to the parent screen instead.
 */
function depth(): number {
  const d = (history.state as { mscDepth?: unknown } | null)?.mscDepth;
  return typeof d === 'number' ? d : 0;
}

export function navigate(path: string, replace = false): void {
  if (replace) history.replaceState({ mscDepth: depth() }, '', path);
  else history.pushState({ mscDepth: depth() + 1 }, '', path);
  void render();
}

/** Does any registered route match `path`? */
export function hasRoute(path: string): boolean {
  return match(path).view !== notFound;
}

/**
 * The back chevron's action.
 *
 * Back through history while there is in-app history to go back through, so
 * the screen returned to is the one the member actually came from. Otherwise
 * the nearest ancestor path that is a real screen: `/groups/80/events` goes to
 * `/groups/80`, while `/users/5` has no `/users` and goes home. `replace`,
 * because pushing the parent would make the NEXT Back return to the child.
 */
export function goBack(): void {
  if (depth() > 0) {
    history.back();
    return;
  }
  const parts = window.location.pathname.split('/').filter(Boolean);
  while (parts.length > 0) {
    parts.pop();
    const candidate = `/${parts.join('/')}`;
    if (candidate !== '/' && hasRoute(candidate)) {
      navigate(candidate, true);
      return;
    }
  }
  navigate('/', true);
}

/**
 * Teardown callbacks for the CURRENT view, run before the next one renders.
 *
 * The router replaces the outlet's children without telling anyone, which is fine
 * for plain DOM but not for anything that outlives it — a WebSocket subscription,
 * a timer, a document-level listener. Without this, navigating away from the chat
 * would leave a handler rendering into detached nodes for the rest of the session.
 */
let leaveHandlers: Array<() => void> = [];

/** Register cleanup for the view being rendered. Call it from the view body. */
export function onLeave(fn: () => void): void {
  leaveHandlers.push(fn);
}

/**
 * Callbacks run after every render, for chrome that lives OUTSIDE the outlet.
 *
 * The nav bar is a sibling of the outlet, not a child, so the router never
 * touches it — which is what keeps it from being rebuilt on every navigation.
 * The cost is that it has no other way to learn the path changed and re-mark its
 * active link. Registered once at boot; unlike onLeave() these are permanent.
 */
const afterRenderHandlers: Array<(path: string) => void> = [];

export function afterRender(fn: (path: string) => void): void {
  afterRenderHandlers.push(fn);
}

export async function render(): Promise<void> {
  // Before the teardown, so a redirect never tears the current view down twice.
  // `replace`, not push: the path being left is one the visitor may not enter,
  // so leaving it in history would make Back a bounce loop. The equality check
  // is a backstop -- a guard that redirected a path to itself would recurse.
  const redirect = guard?.(window.location.pathname) ?? null;
  if (redirect !== null && redirect !== window.location.pathname) {
    navigate(redirect, true);
    return;
  }

  // Tear down the outgoing view first. Each is guarded: one throwing cleanup must
  // not prevent the others from running, or leave the router wedged.
  for (const fn of leaveHandlers) {
    try {
      fn();
    } catch (err) {
      console.error('view teardown failed:', err);
    }
  }
  leaveHandlers = [];

  const { pathname, search } = window.location;
  const { view, params } = match(pathname);
  outlet.replaceChildren();
  await view(outlet, { path: pathname, params, query: new URLSearchParams(search) });

  // After the view, so chrome that reads auth state sees the result of any
  // redirect the view performed. Guarded individually: broken chrome must not
  // take the page down with it.
  for (const fn of afterRenderHandlers) {
    try {
      fn(pathname);
    } catch (err) {
      console.error('afterRender handler failed:', err);
    }
  }

  scrollToHash();
}

/**
 * Scroll to `#id` after a render, when the URL carries one.
 *
 * ⚠ THE ROUTER IGNORED THE HASH ENTIRELY until 2026-09-11, and a browser only
 * scrolls to an anchor on a real page load -- so a same-origin link with a
 * fragment was intercepted, pushed to history, and then silently landed at the
 * top of the page. Nothing throws and the URL looks right, which is why the
 * header's "1 join request" chip appeared to work while dropping the reader
 * two screens above the queue it counted.
 *
 * Deliberately tolerant of a target that is not there YET: a view may paint its
 * shell synchronously and fill it from a fetch, so one retry on the next frame
 * covers the common case without turning this into a polling loop. A hash that
 * matches nothing is not an error -- it is a stale link, and the page is still
 * usable at the top.
 *
 * `scrollIntoView` rather than a manual offset: `--msc-header-h` already exists
 * for sticky positioning, but `scroll-margin-top` in the stylesheet is what
 * keeps a target from hiding UNDER the sticky header, and that belongs in CSS
 * rather than being recomputed here.
 */
function scrollToHash(): void {
  const id = window.location.hash.slice(1);
  if (!id) return;

  const tryScroll = (): boolean => {
    // `getElementById` rather than a selector: a hash can contain characters
    // that are not a valid CSS identifier, and querySelector would throw on it.
    const target = document.getElementById(id);
    if (!target) return false;
    target.scrollIntoView({ block: 'start' });
    return true;
  };

  if (tryScroll()) return;
  requestAnimationFrame(() => {
    tryScroll();
  });
}

export function startRouter(mount: HTMLElement): void {
  outlet = mount;

  // Stamp the entry the app was loaded on, so `depth()` has a floor. A reload
  // keeps an existing stamp, which is right: the history behind it survived too.
  if (typeof (history.state as { mscDepth?: unknown } | null)?.mscDepth !== 'number') {
    history.replaceState({ mscDepth: 0 }, '', window.location.href);
  }

  window.addEventListener('popstate', () => void render());

  // Intercept same-origin link clicks so navigation never reloads the page.
  // This is what keeps the WebSocket alive across views (Phase 8).
  document.addEventListener('click', (ev) => {
    if (ev.defaultPrevented || ev.button !== 0) return;
    if (ev.metaKey || ev.ctrlKey || ev.shiftKey || ev.altKey) return;
    const anchor = (ev.target as HTMLElement | null)?.closest('a');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    // ONLY in-app paths. This used to skip just `http…` and `//…`, so every
    // other scheme (`sms:`, `tel:`, `mailto:`) was cancelled and "navigated" to
    // inside the SPA, which does nothing: Open Messages on a phone invite, and
    // the call, text and email links on Contacts and profiles, were all dead taps
    // (found on an iPhone, 2026-10-15). Anything that is not a path starting with
    // a single slash belongs to the browser or the OS.
    if (!href || !href.startsWith('/') || href.startsWith('//')) return;
    if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;
    ev.preventDefault();
    navigate(href);
  });

  void render();
}
